/**
 * Every Task Action string: the My Day status pills (P2) and the task panel's Next action banner (P3), verbatim from
 * the story (§8-§10, §61-§80, §84, §86) as tabled in MYDAY_LEAD_COMMS_PLAN.md Appendix B.4 (FE-05). One table, so a
 * row's pill and its panel's banner can never word the same state two ways.
 *
 * Typography (S-33): `’` is U+2019, ` · ` is U+00B7 with one space each side, `—` is U+2014. The file is UTF-8 without
 * a BOM; a mojibake grep over it must find nothing (T-A8). Tokens in braces are filled by `fillTaskActionCopy`.
 * The stage's own Next Step and its notes (LP, P6) are tenant configuration, not copy, so they are not here.
 */
export const TASK_ACTION_COPY = {
    /** Rendered uppercase by CSS: NEXT ACTION (§61). "Next step" never appears in the banner. */
    EYEBROW: 'Next action',
    REGION_LEAD: 'Lead workflow',
    REGION_NONLEAD: 'Next action',
    RAIL_LABEL: 'Lead stage',
    /** Headlines. */
    H: {
        replied: 'Review {first}’s reply and respond',
        /** P3 interim New-lead headline until LP's stage Next Step (§65, PR7). */
        leadNew: 'Make first contact with {first}',
        /** P3 interim follow-up / Reminder / stage-default headline (§83 safe fallback, PR7). */
        leadInterim: 'Follow up with {first}',
        waiting: 'Follow up with {first}',
        waitingAbout: 'Follow up with {first} about {subject}',
        /** PR31: the deterministic fallback for an unanswered first email (the AI Next Action replaces it when it has one). */
        needsReply: 'Reply to {first}',
        needsReplyAbout: 'Reply to {first} about {subject}',
    },
    /** Lead Pipeline LP5: the Lead banner's links (D4's last branch, TQ19's completed Primary). */
    LP: {
        addNextStep: '+ Add next step',
        addNextStepTip: 'Set the next step for every lead in this stage',
        setFollowUp: 'Set follow-up',
    },
    /** Supporting guidance (deterministic, §84). */
    G: {
        leadReplied: 'Read the latest message in Conversations below, then reply or update the Lead as appropriate.',
        leadEmailFollowUp:
            'No reply since your last email. Send a short check-in or call, then set another follow-up if needed.',
        /** PR30: a Lead in Follow up whose newest touch was NOT a sent email (Handled another way, a call, a note). */
        leadFollowUpOther: 'Follow up with {first} and record what happened. Set another follow-up if needed.',
        leadReminder:
            'Follow up with {first} and record what happened. Set another reminder if more follow-up is needed.',
        replied: 'Read the latest message in Conversations below, then respond or complete the task as appropriate.',
        /**
         * PR31: an unanswered first email. Names no one: the email may come from someone other than the linked client (a
         * referrer writing about them, live QA 42656), and it says nothing about a reply it cannot verify.
         */
        needsReply: 'Read the latest email in Conversations below, then reply.',
        waiting: 'No reply since your last email. Send a follow-up if you still need {first}’s response.',
        /** A Project/Case Email whose client is a company: no first name to use. */
        waitingNoName: 'No reply since your last email. Send a follow-up if you still need their response.',
        clientReminder: 'Follow up with {first} and record what happened. Set another reminder if needed.',
        projectReminder: 'Follow up and record what happened. Set another reminder if more action is needed.',
        /** A Client communications / non-Primary Leads Reminder with no usable first name (legacy data only, U4). */
        reminderFallback: 'Follow up and record what happened. Set another reminder if needed.',
    },
    /**
     * User 2026-10-26 (TCS NXG-04): the Lead guidance lines, short, for the Pipeline card's next action when it has no step, no AI
     * action and nothing generating - the same key as the panel's long line (`leadGuidanceKeyOf`), at most 40 characters, no name.
     */
    GS: {
        leadReplied: 'Read the reply and respond',
        leadEmailFollowUp: 'No reply yet. Check in or call',
        leadFollowUpOther: 'Follow up and log what happened',
        leadReminder: 'Follow up and log what happened',
    },
    /** Banner metadata (§86): always "Label · ago". */
    M: {
        newLead: 'New lead · {ago}',
        replied: 'Client replied · {ago}',
        /** PR31: only from a stamped inbound instant - never the task's creation time (no invented metadata). */
        received: 'Received · {ago}',
        lastContacted: 'Last contacted {date} · {since}',
        lastActivity: 'Last activity {date} · {since}',
    },
    /** My Day status pills (§8-§10; plan A.2.2). A missing age leaves the bare label, never a guess (§85). */
    P: {
        confirmLead: 'Confirm lead · {ago}',
        newLead: 'New lead · {ago}',
        clientReplied: 'Client replied · {ago}',
        followUp: 'Follow up',
        unverifiedSender: 'Unverified sender · attachment held',
        automation: 'Two automations could fit · {ago}',
    },
    /** Black tooltips. */
    TIP: {
        headline: 'Show {first}’s latest reply',
        /** PR31: the unanswered first email's headline link. */
        headlineEmail: 'Show {first}’s email',
        current: 'Current stage',
        move: 'Move to {stage}',
        pill: '{text} — open the task',
        /** LP §4.20 LFC-12: a Leads Primary row with no follow-up (handoff (14) rowAction rule 3); bare with no touch on record. */
        noFollowUp: 'No follow-up set · last activity {day} ({ago}) — open the task',
        noFollowUpBare: 'No follow-up set — open the task',
    },
    /** The rail pills' accessible names (B.1 `StagePillVm.ariaLabel`). */
    ARIA: {
        current: '{stage}, current stage',
        move: 'Move {first} to {stage}',
    },
    /** `{first}` of a Lead whose person has no usable name (B.5 step 3, D:10580). */
    NAME: {
        lead: 'the lead',
        /** The Lost toast's `{name}` when the lead has no name at all: it starts the sentence. */
        leadSentence: 'The lead',
    },
    MORE: 'More',
    LESS: 'Less',
    /**
     * The rail's trailing outcome pill and its `Lead outcome` popover (handoff 5, Appendix I.2; PR21-PR24): D5:2287-2323.
     * The design's footnote names its AI ("Helps Leo …"); the app's AI is "Ask AI", so the name is dropped (PR24).
     */
    OUTCOME: {
        pill: 'Other stage',
        tooltip: 'Mark Won or Lost',
        /** While the rail leaves stages out, the menu holds them too (its `Move to` section; user, 2026-10-09). */
        tooltipMore: 'More stages, Won or Lost',
        dialog: 'Lead outcome',
        /** Rendered uppercase by CSS: CLOSE THE LEAD. */
        menuTitle: 'Close the lead',
        /**
         * Rendered uppercase by CSS: MOVE TO. The menu's first section, shown only when the rail has no room for every
         * Open stage: the stages it left out (live QA 2026-10-09).
         */
        moveTo: 'Move to',
        metaCurrent: 'Current',
        metaWon: 'Case handoff next',
        /** PR23: every Lost stage asks until LP's stage configuration has a default reason. */
        metaAskWhy: 'Ask why',
        /** PR23: a Lost stage with a configured default reason (LP, O1). Not produced yet. */
        metaReason: 'Reason: {reason}',
        whyTitle: 'Why was {first} lost?',
        whySub: 'Moves to {stage}',
        whyStep: 'Step 2 of 2',
        whyReady: 'Ready',
        back: 'Back',
        skip: 'Skip reason',
        confirm: 'Mark lost',
        footnote: 'Helps show where and why leads drop off.',
    },
    /** The reason pills' radiogroup and the `Why?` block above them (I.3, I.4; PR24). */
    LOST_WHY: {
        group: 'Lost reason',
        /** Rendered uppercase by CSS: WHY?. */
        label: 'Why?',
        hintIdle: 'Optional — helps show why leads drop off.',
        hintPicked: 'Reason saved.',
    },
    TOAST: {
        moved: 'Moved to {stage}',
        /** A Lost move that completed the task (PR25; the design's `leadTransition` Lost branch, D5:8925). */
        markedLost: '{name} marked Lost',
        markedLostSub: 'Lead task completed and follow-up stopped.',
    },
    ERR: {
        stage: 'Couldn’t change the stage. Try again.',
        /** `TaskLeadStage/SetLostReason` failed after the relationship popover moved the stage (FE-140). */
        lostReason: 'The stage moved, but the lost reason wasn’t saved.',
    },
    /**
     * Smart Scheduling (check-in 3, decision Q7; story §28): the task panel's one reason line when an automatic booking
     * didn't happen, keyed by the server's `SchedulingReason` code (Core.Shared/Scheduling/SchedulingBookingDecisions.cs).
     * `{name}` is a first name; the `*NoName` lines stand in when the server has none, so no sentence loses its subject.
     */
    SCHED: {
        timeNoLongerFree: 'Not booked: the agreed time is no longer free.',
        personBusy: 'Not booked: {name} is busy at that time.',
        personBusyNoName: 'Not booked: a colleague is busy at that time.',
        organizerUnclear: 'Not booked: couldn’t tell whose calendar the meeting goes on.',
        cannotBookFor: 'Not booked: {name}’s calendar can’t be booked from this account.',
        cannotBookForNoName: 'Not booked: that calendar can’t be booked from this account.',
        outlookNotConnected: 'Not booked: {name}’s Outlook isn’t connected.',
        outlookNotConnectedNoName: 'Not booked: Outlook isn’t connected.',
        outlookDidNotCreate: 'Not booked: Outlook didn’t create the meeting.',
        notOffered: 'Not booked: the accepted time isn’t one that was offered.',
        replyUnclear: 'Not booked: the reply doesn’t clearly agree on one time.',
        calendarOff: 'Not booked automatically: the Calendar is turned off for this workspace.',
        timePassed: 'Not booked: that time has already passed.',
        maybeAlreadyBooked: 'Not booked: a meeting at that time may already exist.',
        calendarCheckFailed: 'Not booked: the calendars couldn’t be checked.',
        offerNotFound: 'Not booked: couldn’t find the times that were offered.',
        personUnclear: 'Not booked: more than one colleague has that name.',
        bookingRefused: 'Not booked: Trovve couldn’t create the meeting.',
        clientUnknown: 'Not booked: couldn’t tell who to invite.',
        alreadyBookedOtherTime: 'Not booked: there’s already a meeting at another time. Move it in the calendar.',
        moveNotAllowed: 'Not moved: this account can’t change that meeting.',
        meetingToMoveUnclear: 'Not moved: couldn’t tell which meeting to move.',
        meetingToMoveGone: 'Not moved: couldn’t find the meeting to move.',
        meetingToCancelUnclear: 'Not cancelled: couldn’t tell which meeting to cancel.',
    },
    /**
     * Smart Scheduling addendum 3 (PM 4 Nov; SMART_SCHEDULING_ADDENDUM3_BANNER_AND_INVITES.md D4, D8 - D10): the Next action
     * banner's scheduling headline and line, and the client's invitation. Names, never pronouns. The button's own label is the
     * Reply box pill's, from the server.
     */
    SCHED_BANNER: {
        proposeHeadline: 'Propose times to {name}',
        askWindow: '{Name} asked to meet {window}. Pick when you’re free and we’ll write the email.',
        askOpen: '{Name} wants to find a time to meet. Pick when you’re free and we’ll write the email.',
        askMove: '{Name} asked to move the meeting. Pick when you’re free and we’ll write the email.',
        askPassed: 'The time {name} proposed has passed. Pick when you’re free and we’ll write the email.',
        timeHeadline: 'Respond to {name}’s proposed time',
        timesHeadline: 'Respond to {name}’s proposed times',
        moveHeadline: 'Respond to {name}’s new time',
        oneTime: '{Name} proposed {when}.',
        severalTimes: '{Name} proposed a few times.',
        moveTo: '{Name} asked to move the meeting to {when}.',
        inviteHeadline: '{Name} already set up a meeting',
        inviteFree: '{subject} on {day} at {time}. You’re free then. Say yes or no.',
        inviteBusy: '{subject} on {day} at {time}. You have something else then. Say yes or no.',
        inviteSubjectFallback: 'A meeting',
        inviteFrom: 'Invite from {name} · {ago}',
        declineConfirm: 'Decline {name}’s meeting? It will be removed from your calendar.',
        acceptedTitle: 'You said yes to {name}’s meeting',
        acceptedText: 'It’s on your calendar. {Name} will see that you’re coming.',
        declinedTitle: 'You said no to {name}’s meeting',
        declinedText: '{Name} will see that you can’t make it. Send {name} a time that works better.',
        /** No first name: "{Name}" at a sentence's start, "{name}" inside it. */
        clientStart: 'The client',
        clientMid: 'the client',
        accept: 'Accept',
        decline: 'Decline',
        keep: 'Keep',
        change: 'Change',
        suggest: 'Suggest a new time',
        acceptError: 'Couldn’t answer the invitation in Outlook. Try again.',
        declineError: 'Couldn’t decline the invitation. Try again.',
    },
} as const;

/** The one separator between a label and its age or date (U+00B7 with a space each side). */
export const TASK_ACTION_SEPARATOR = ' · ';

/** Token values for `fillTaskActionCopy`. A null, undefined or empty value leaves its token out. */
export type TaskActionCopyTokens = Readonly<Record<string, string | null | undefined>>;

/** A `{token}` and the separator or single space in front of it. */
const TOKEN_PATTERN = /( · | )?\{(\w+)\}/g;

/**
 * Fills a copy template's `{token}`s. A token with no value is dropped together with the ` · ` (or the space) in front
 * of it, so 'Client replied · {ago}' with no age reads 'Client replied' (§85: never a guessed age).
 */
export function fillTaskActionCopy(template: string, tokens: TaskActionCopyTokens = {}): string {
    const values = tokens || {};
    return (template || '')
        .replace(TOKEN_PATTERN, (_match: string, prefix: string | undefined, name: string) => {
            const value = values[name];
            return value === null || value === undefined || value === '' ? '' : `${prefix || ''}${value}`;
        })
        .trim();
}
